import { useTranslation } from 'react-i18next'
import { useStorePlatform } from '@/hooks'
import {
  useExchangesShown,
  useFullCatalogShown,
  useInstantExchangeShown
} from '@/hooks/use-scope-platform'
import { useAppSelector } from '@/redux/hooks'
import { seesEveryGroup } from '@/domain/store-group'
import { nonReturnableListsFor } from '@/application/manage-return-conditions'
import { TabProps } from '@/ui/shared'
import { useReturnsAndRefundsV2 } from '@/hooks/use-returns-and-refunds-v2'

// Each Configuration menu entry owns one tab bar; a page highlights its own tab in it.
export function useReturnConditionsTabs(): TabProps[] {
  const { t } = useTranslation()
  const platform = useStorePlatform()
  // Group-level return conditions keep every non-returnable list and add the requirements.
  const { conditions: groupLevel } = useReturnsAndRefundsV2()
  const tab = (link: string) => ({ link, copy: t(`page_titles.${link}`) })
  return [
    tab('/configuration/return-conditions'),
    tab('/configuration/reasons'),
    ...(groupLevel || nonReturnableListsFor(platform).length > 0
      ? [tab('/configuration/non-returnable')]
      : []),
    ...(groupLevel ? [tab('/configuration/requirements')] : [])
  ]
}

export function useLogisticsTabs(): TabProps[] {
  const { t } = useTranslation()
  return [
    {
      link: '/configuration/shipping',
      copy: t('page_titles./configuration/shipping')
    }
  ]
}

// Instant exchange is REVER's to set: merchants don't see its tab.
export function useExchangesTabs(): TabProps[] {
  const { t } = useTranslation()
  const user = useAppSelector((s) => s.userApi.getMe.response.user)
  const showCatalog = useFullCatalogShown()
  const showInstant = useInstantExchangeShown() && seesEveryGroup(user)
  return [
    { link: '/configuration/exchanges', copy: t('exchanges.tabs.general') },
    {
      link: '/configuration/exchanges/orders',
      copy: t('exchanges.tabs.orders')
    },
    ...(showCatalog
      ? [
          {
            link: '/configuration/exchanges/full-catalog',
            copy: t('exchanges.tabs.catalog')
          }
        ]
      : []),
    ...(showInstant
      ? [
          {
            link: '/configuration/exchanges/instant-exchange',
            copy: t('exchanges.tabs.instant'),
            badge: t('exchanges.rever_only')
          }
        ]
      : [])
  ]
}

// Refunds and Exchanges share one tab bar once the group's refund configuration is group-level; the exchanges
// tabs then sit in a row under it. Exchanges only where the group's platforms offer them.
export function useRefundsTabs(): TabProps[] {
  const { t } = useTranslation()
  const exchangesShown = useExchangesShown()
  return [
    {
      link: '/configuration/refunds',
      copy: t('page_titles./configuration/refunds')
    },
    ...(exchangesShown
      ? [
          {
            link: '/configuration/exchanges',
            copy: t('page_titles./configuration/exchanges')
          }
        ]
      : [])
  ]
}

// The withdrawal form is REVER's to turn on, like instant exchange: merchants don't see its tab.
export function useCustomisationTabs(): TabProps[] {
  const { t } = useTranslation()
  const user = useAppSelector((s) => s.userApi.getMe.response.user)
  return [
    { link: '/configuration/theme', copy: t('drawer_pages.custom-theme') },
    { link: '/configuration/copies', copy: t('drawer_pages.custom-copies') },
    ...(seesEveryGroup(user)
      ? [
          {
            link: '/configuration/withdrawal',
            copy: t('withdrawal_settings.tab'),
            badge: t('exchanges.rever_only')
          }
        ]
      : [])
  ]
}

export function useWarehousesTabs(): TabProps[] {
  const { t } = useTranslation()
  return [
    { link: '/configuration/warehouses', copy: t('warehouses.tabs.list') },
    {
      link: '/configuration/warehouses/rules',
      copy: t('warehouses.tabs.rules')
    }
  ]
}
